import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast } from "react-toastify";
import ActionButton from "../../../shared/ActionButton";
import { DeliverySettingCard } from "./DeliverySettingCard";
import useMailMagazineSchedules from "../../hooks/useMailMagazineSchedules";

// settings 配列のインデックスとカテゴリの対応定義
const SETTING_DEFS = [
  { title: "新着案件", category: "project" },
  { title: "お役立ち情報", category: "useful_info" },
];

// 曜日・時刻はなく、ON/OFFのみ切り替えられる配信
const TOGGLE_ONLY_SETTING_DEFS = [
  { title: "毎月25日配信", category: "recruiting_monthly" },
];

// API 取得前に表示するデフォルト値
const FALLBACK_SETTINGS = SETTING_DEFS.map(({ title }) => ({
  title,
  weekdays: [0],
  hour: "09",
  minute: "00",
  active: true,
}));

function settingsEqual(a, b) {
  return (
    a.weekdays.length === b.weekdays.length &&
    a.weekdays.every((d, i) => d === b.weekdays[i]) &&
    a.hour === b.hour &&
    a.minute === b.minute
    // active は即時保存のため dirty 判定に含めない
  );
}

function validate(settings) {
  const errors = [];
  settings.forEach((s) => {
    if (s.weekdays.length === 0) {
      errors.push(`「${s.title}」の配信曜日を1日以上選択してください`);
    }
    if (!s.hour || !s.minute) {
      errors.push(`「${s.title}」の配信時刻を設定してください`);
    }
  });
  return errors;
}

export default function Schedule() {
  const {
    loading,
    saving,
    fetchSchedules,
    saveSchedules,
    apiSchedulesToSettings,
    toggleActive,
  } = useMailMagazineSchedules();

  const [settings, setSettings] = useState(FALLBACK_SETTINGS);

  // ON/OFFのみ管理する配信設定
  const [toggleOnlySettings, setToggleOnlySettings] = useState(
    TOGGLE_ONLY_SETTING_DEFS.map(({ title }) => ({
      title,
      active: true,
    })),
  );

  const [baseline, setBaseline] = useState(FALLBACK_SETTINGS);
  const [initialized, setInitialized] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const loadSchedules = useCallback(
    async (isMounted = () => true) => {
      const mountedCheck =
        typeof isMounted === "function" ? isMounted : () => true;
      setLoadError(false);
      const apiSchedules = await fetchSchedules();
      if (!mountedCheck()) return;

      if (!apiSchedules) {
        setLoadError(true);
        setInitialized(true);
        return;
      }

      const loaded = apiSchedulesToSettings(apiSchedules, SETTING_DEFS);
      if (!mountedCheck()) return;
      setSettings(loaded);
      setBaseline(loaded);

      // ON/OFFのみの配信設定を反映
      setToggleOnlySettings(
        TOGGLE_ONLY_SETTING_DEFS.map(({ title, category }) => ({
          title,
          active: apiSchedules?.[category]?.active ?? true,
        })),
      );

      setInitialized(true);
    },
    [fetchSchedules, apiSchedulesToSettings],
  );

  // 初回マウント時に最新スケジュールを取得して設定に反映
  useEffect(() => {
    let mounted = true;

    (async () => {
      await loadSchedules(() => mounted);
    })();

    return () => {
      mounted = false;
    };
  }, [loadSchedules]);

  const updateSetting = (index, patch) =>
    setSettings((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    );

  const handleToggleActive = async (index, active) => {
    const { category, title } = SETTING_DEFS[index];
    const prevActive = settings[index]?.active;
    updateSetting(index, { active });
    const result = await toggleActive(category, active, title);
    if (!result) {
      updateSetting(index, { active: prevActive });
      return;
    }

    if (!result.schedules) return;

    const nextActive = result.schedules?.[category]?.active ?? active;
    updateSetting(index, { active: nextActive });
  };

  const hasEmptyWeekdays = settings.some((s) => s.weekdays.length === 0);
  const isDirty = settings.some((s, i) => !settingsEqual(s, baseline[i]));
  const isBusy = loading || saving;
  const disabled = loadError || !isDirty || hasEmptyWeekdays || isBusy;

  const tooltipTitle = hasEmptyWeekdays
    ? "配信曜日を1日以上選択してください"
    : !isDirty
      ? "変更がありません"
      : "";

  const handleSave = async () => {
    const errors = validate(settings);
    if (errors.length > 0) {
      errors.forEach((msg) => toast.error(msg));
      return;
    }

    const result = await saveSchedules(settings, SETTING_DEFS);
    if (!result) return;

    // 保存後はサーバーから返ってきたデータで状態を同期し、dirty をリセット
    // schedules が null の場合はレスポンス読み取り失敗（保存自体は成功）なので
    // 現在の settings をそのままベースラインにする
    if (!result.schedules) {
      setBaseline(settings);
      return;
    }
    const updated = apiSchedulesToSettings(result.schedules, SETTING_DEFS);
    setSettings(updated);
    setBaseline(updated);
  };

  // ON/OFFのみの配信設定を更新
  const handleToggleOnlyActive = async (index, active) => {
    const { category, title } = TOGGLE_ONLY_SETTING_DEFS[index];
    const prevActive = toggleOnlySettings[index].active;

    // 先に画面を更新
    setToggleOnlySettings((prev) =>
      prev.map((setting, i) =>
        i === index ? { ...setting, active } : setting,
      ),
    );

    const result = await toggleActive(category, active, title);

    // 保存失敗時は元に戻す
    if (!result) {
      setToggleOnlySettings((prev) =>
        prev.map((setting, i) =>
          i === index ? { ...setting, active: prevActive } : setting,
        ),
      );
      return;
    }

    // サーバー側の値を反映
    if (result.schedules) {
      setToggleOnlySettings((prev) =>
        prev.map((setting, i) =>
          i === index
            ? {
                ...setting,
                active: result.schedules?.[category]?.active ?? active,
              }
            : setting,
        ),
      );
    }
  };

  if (!initialized) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Typography color="error.main">
          配信設定の取得に失敗しました。再読み込みしてから操作してください。
        </Typography>
        <ActionButton
          label="再読み込み"
          onClick={() => loadSchedules()}
          disabled={loading}
        />
      </Stack>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {settings.map((setting, index) => (
        <DeliverySettingCard
          key={setting.title}
          title={setting.title}
          weekdays={setting.weekdays}
          hour={setting.hour}
          minute={setting.minute}
          active={setting.active}
          disabled={isBusy || setting.weekdays.length === 0}
          inputsDisabled={isBusy}
          onWeekdaysChange={(weekdays) => updateSetting(index, { weekdays })}
          onHourChange={(hour) => updateSetting(index, { hour })}
          onMinuteChange={(minute) => updateSetting(index, { minute })}
          onActiveChange={(active) => handleToggleActive(index, active)}
        />
      ))}

      {/* ON/OFFのみ管理する配信 */}
      {toggleOnlySettings.map((setting, index) => (
        <DeliverySettingCard
          key={setting.title}
          title={setting.title}
          hideScheduleInputs
          active={setting.active}
          disabled={isBusy}
          onActiveChange={(active) => handleToggleOnlyActive(index, active)}
        />
      ))}

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="body2" color="text.secondary">
            設定変更は次回配信から反映されます。配信開始30分前まで編集可能です。
          </Typography>
        </Stack>

        <Tooltip title={tooltipTitle}>
          <span>
            <ActionButton
              label={saving ? "保存中..." : "設定を保存"}
              onClick={handleSave}
              disabled={disabled}
              sx={{
                bgcolor: disabled
                  ? "action.disabledBackground"
                  : "primary.main",
                color: disabled ? "text.disabled" : "#fff",
                "&:hover": {
                  bgcolor: disabled
                    ? "action.disabledBackground"
                    : "primary.dark",
                },
              }}
            />
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
}
